import { useState } from 'react';

import { useLetterhead } from '@/api/bookings';
import { useCashDayBooks, type CashAccountBalance } from '@/api/cash';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass } from '@/components/Field';
import { formatDate } from '@core/format/dates';
import { dayBookSheet } from '@core/print/sheetHtml';
import { SheetPrint } from '../print/SheetPrint';

/**
 * The day book (Cash &amp; bank step 8) — every account on the board for one day, each its own page,
 * on the four papers through the shared print preview. The day can be changed in the preview, so last
 * night's book can be printed in the morning. Every figure is the server's (`…/day-book`).
 */
export function DayBookPrint({
  branchId,
  accounts,
  today,
  onPrinted,
  onClose,
}: {
  branchId: number | undefined;
  accounts: CashAccountBalance[];
  today: string;
  /** Told the day printed — the board's Close the day step. */
  onPrinted?: (date: string) => void;
  onClose: () => void;
}) {
  const [date, setDate] = useState(today);
  const letterhead = useLetterhead(branchId);
  const { books, error } = useCashDayBooks(
    branchId,
    accounts.map((account) => account.id),
    date,
  );
  const [printedAt] = useState(() => new Date().toISOString());

  const dayPicker = (
    <Field label="Day">
      <input
        type="date"
        max={today}
        value={date}
        onChange={(event) => setDate(event.target.value || today)}
        className={controlClass}
      />
    </Field>
  );

  if (!books || !letterhead.data) {
    return (
      <div className="flex flex-col gap-3 lg:gap-4">
        <PageHeading
          title="Day book"
          subtitle={formatDate(date)}
          actions={
            <Button onClick={onClose} shortcut="back">
              Back
            </Button>
          }
        />
        {error || letterhead.error ? (
          <Notice tone="danger" title="The day book could not be read">
            {(() => {
              const failure = error ?? letterhead.error;

              return failure instanceof Error ? failure.message : 'Try again in a moment.';
            })()}
          </Notice>
        ) : (
          <Card>
            <p className="text-default text-[length:var(--scale-list-meta)]">Reading the day…</p>
          </Card>
        )}
      </div>
    );
  }

  const shop = letterhead.data;

  return (
    <SheetPrint
      eyebrow="Day book"
      title={formatDate(date)}
      remember="attirerent:paper:day-book"
      extra={dayPicker}
      build={(paper, rollLength) =>
        dayBookSheet({ letterhead: shop, date, books, printedAt }, paper, rollLength)
      }
      onPrinted={() => onPrinted?.(date)}
      onClose={onClose}
    />
  );
}
